// Match Lab spatial view: tracking frames fetched a chunk at a time.
//
// The render loop asks for the playhead's chunk on every animation frame,
// so this must answer "already loaded / in flight / backing off" cheaply
// and must FAIL CLOSED: a 401, a 500 or a non-array body used to leave the
// chunk unmarked, and the loop re-requested it ~60 times a second against
// an endpoint that reads R2 (SDET 82.0, #463). A failed chunk now waits
// before it is tried again, twice as long each time, up to a minute.

export type TrackingPlayer = { team: number; jersey: number; x: number; y: number; speed?: number }
export type TrackingBall = { x: number; y: number; z: number; speed?: number }
export type CachedFrame = { players: TrackingPlayer[]; ball: TrackingBall | null }

export const FIRST_BACKOFF_MS = 2000
export const MAX_BACKOFF_MS = 60000

export class TrackingChunks {
  readonly frames = new Map<number, CachedFrame>()
  private loaded = new Set<number>()
  private inflight = new Set<number>()
  /** start -> { next attempt allowed at, backoff used } */
  private failed = new Map<number, { at: number; wait: number }>()

  constructor(
    private url: (start: number, count: number) => string,
    readonly chunk: number,
    private doFetch: typeof fetch = (...a) => fetch(...a),
    private now: () => number = () => Date.now(),
  ) {}

  /** Start loading the chunk holding `frame`, unless it is loaded, in
   *  flight, or backing off after a failure. */
  ensure(frame: number): void {
    if (frame < 0) return
    const start = Math.floor(frame / this.chunk) * this.chunk
    if (this.loaded.has(start) || this.inflight.has(start)) return
    const f = this.failed.get(start)
    if (f && this.now() < f.at) return
    this.inflight.add(start)
    this.doFetch(this.url(start, this.chunk), { credentials: 'same-origin' })
      .then(async (r) => {
        if (!r.ok) throw new Error(`tracking-frames ${start}: HTTP ${r.status}`)
        const body: unknown = await r.json()
        if (!Array.isArray(body)) throw new Error(`tracking-frames ${start}: not a list`)
        for (const fr of body as ({ frame: number } & CachedFrame)[]) {
          this.frames.set(fr.frame, { players: fr.players, ball: fr.ball })
        }
        this.loaded.add(start)
        this.failed.delete(start)
      })
      .catch(() => {
        const wait = Math.min(MAX_BACKOFF_MS,
                              (f?.wait ?? FIRST_BACKOFF_MS / 2) * 2)
        this.failed.set(start, { at: this.now() + wait, wait })
      })
      .finally(() => this.inflight.delete(start))
  }

  isLoaded(frame: number): boolean {
    return this.loaded.has(Math.floor(frame / this.chunk) * this.chunk)
  }

  /** Keep only chunks within `keep` chunks of `frame`. */
  prune(frame: number, keep: number): void {
    if (this.frames.size <= 2 * keep * this.chunk) return
    const far = (k: number) => Math.abs(k - frame) > keep * this.chunk
    for (const k of this.frames.keys()) if (far(k)) this.frames.delete(k)
    for (const c of this.loaded) if (far(c)) this.loaded.delete(c)
  }

  clear(): void {
    this.frames.clear(); this.loaded.clear(); this.failed.clear()
  }
}
